import { consumer } from "@lib/cable";
import { useChatStore } from "@lib/stores/chatStore";
import { track } from "@lib/telemetry";
import type { ChatMessage, ChatPlan, ChatQuestion, ChatTask } from "@lib/types";
import { useEffect, useRef } from "react";

// Live chat wiring: see docs/live-ui-sync.md §6.5.
//
// Dispatches ChatChannel broadcasts into chatStore, and keeps the store honest
// when broadcasts are lost. Every payload carries a per-thread `seq`: a stale
// seq is dropped, a gap schedules an incremental resync. A *re*connect (deploy,
// Puma restart, laptop sleep) or the tab regaining visibility resyncs the open
// thread, and the subscription's health is mirrored into
// chatStore.connectionState for the header indicator.
export function useChatChannel(sessionId: string | null) {
  const subscriptionRef = useRef<ReturnType<typeof consumer.subscriptions.create>>(null);

  useEffect(() => {
    if (!sessionId) return;

    const store = useChatStore.getState;
    let everConnected = false;
    let wasHidden = false;
    let disconnectedAt: number | undefined;

    const resyncCurrent = () => {
      const { currentThreadId, resyncThread } = store();
      if (currentThreadId) void resyncThread(currentThreadId);
    };

    subscriptionRef.current = consumer.subscriptions.create(
      { channel: "ChatChannel", session_id: sessionId },
      {
        received(data: Record<string, unknown>) {
          const {
            addMessage,
            updateThreadStatus,
            updateThreadTitle,
            fetchMessages,
            acceptBroadcast,
          } = store();
          const threadId = data.thread_id as string;

          // Streamed reply text (#3162). Its seq is the stream's own counter,
          // not the thread's broadcast_seq, so it never touches acceptBroadcast.
          if (data.action === "message_delta") {
            store().appendStreamDelta(
              threadId,
              data.message_id as string,
              data.seq as number,
              data.text as string,
            );
            return;
          }

          const seq = typeof data.seq === "number" ? data.seq : undefined;
          // new_message is idempotent by id, so it is applied even when its seq
          // is stale; the seq is still recorded so gaps are detected.
          const fresh = acceptBroadcast(threadId, seq);

          if (data.action === "new_message") {
            addMessage({
              id: data.message_id as string,
              thread_id: threadId,
              role: data.role as "user" | "assistant" | "system",
              content: data.content as string,
              tool_use: data.tool_use as boolean | undefined,
              tool_name: data.tool_name as string | undefined,
              tool_result: data.tool_result as boolean | undefined,
              input_tokens: data.input_tokens as number | undefined,
              attachments: data.attachments as ChatMessage["attachments"],
              rich_text_artefacts: data.rich_text_artefacts as ChatMessage["rich_text_artefacts"],
              created_at: data.created_at as string,
            });
            return;
          }

          if (!fresh) return;

          if (data.action === "status_update") {
            updateThreadStatus(
              threadId,
              data.status as string,
              data.current_phase as string,
              data.current_tool_name as string | undefined,
              "active_plan" in data ? ((data.active_plan as ChatPlan | null) ?? null) : undefined,
              "pending_question" in data
                ? ((data.pending_question as ChatQuestion | null) ?? null)
                : undefined,
              "plan_mode_enabled" in data ? (data.plan_mode_enabled as boolean) : undefined,
              "task_list" in data ? ((data.task_list as ChatTask[]) ?? []) : undefined,
            );
          } else if (data.action === "title_update") {
            updateThreadTitle(threadId, data.title as string);
          } else if (data.action === "thread_compacted") {
            if (store().currentThreadId === threadId) {
              fetchMessages(threadId);
            }
          }
        },
        connected() {
          store().setConnectionState("connected");
          if (!everConnected) {
            everConnected = true;
            return;
          }
          // A reconnect, not the first connection: record how long the tab
          // was without live updates (#3161), then catch up.
          track("cable_connected", {
            thread_id: store().currentThreadId ?? undefined,
            downtime_ms: disconnectedAt === undefined ? 0 : Date.now() - disconnectedAt,
          });
          disconnectedAt = undefined;
          // Deltas sent while the socket was down are gone for good; the
          // resync brings the persisted message instead (#3162).
          const { currentThreadId, clearStreaming } = store();
          if (currentThreadId) clearStreaming(currentThreadId);
          resyncCurrent();
        },
        disconnected() {
          if (disconnectedAt === undefined) {
            disconnectedAt = Date.now();
            track("cable_disconnected", { thread_id: store().currentThreadId ?? undefined });
          }
          store().setConnectionState("reconnecting");
        },
        rejected() {
          store().setConnectionState("rejected");
        },
      },
    );

    let visibilityHandler: (() => void) | undefined;
    if (typeof document !== "undefined") {
      visibilityHandler = () => {
        if (document.visibilityState === "hidden") {
          wasHidden = true;
        } else if (document.visibilityState === "visible" && wasHidden) {
          wasHidden = false;
          resyncCurrent();
        }
      };
      document.addEventListener("visibilitychange", visibilityHandler);
    }

    return () => {
      if (visibilityHandler) {
        document.removeEventListener("visibilitychange", visibilityHandler);
      }
      subscriptionRef.current?.unsubscribe();
      subscriptionRef.current = null;
    };
  }, [sessionId]);
}
